import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Radio, Menu, X, Crown, Fuel } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const BRAND_LOGO =
  "https://customer-assets.emergentagent.com/job_journey-hub-283/artifacts/6ld7fx8l_file_00000000ae8871f5b0415f16ffc8ce51~2.png";

const LINKS = [
  { to: "/#the-journey", label: "Journey" },
  { to: "/feed", label: "Field Notes" },
  { to: "/#worth-the-stop", label: "Worth the Stop" },
  { to: "/bucket-list", label: "Bucket List" },
  { to: "/meetups", label: "Meetups" },
  { to: "/shop", label: "Shop" },
  { to: "/lounge", label: "Lounge" },
  { to: "/chat", label: "BooBoo AI" },
];

function useFuelTotals() {
  const [fuel, setFuel] = useState(null);
  useEffect(() => {
    let mounted = true;
    const load = () =>
      api
        .get("/fuel/totals")
        .then((r) => mounted && setFuel(r.data))
        .catch((err) => {
          console.warn("navbar: fuel totals failed", err?.response?.status || err?.message);
        });
    load();
    const t = setInterval(load, 60000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);
  return fuel;
}

function BrandLink() {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
      <img
        src={BRAND_LOGO}
        alt="FMAA"
        className="h-10 w-10 object-cover rounded-sm border border-white/10"
      />
      <div>
        <div className="font-display text-2xl tracking-wider leading-none">FMAA</div>
        <div className="text-[9px] tracking-[0.32em] uppercase text-[#FF4D8B] mt-1">
          FuelMeh Across America
        </div>
      </div>
    </Link>
  );
}

function DesktopNavLinks() {
  return (
    <nav className="hidden md:flex items-center gap-6">
      {LINKS.map((l) => {
        const isHash = l.to.includes("#");
        const commonCls =
          "text-xs lg:text-sm uppercase tracking-[0.18em] font-semibold transition-colors text-white/80 hover:text-[#FF4D8B]";
        if (isHash) {
          return (
            <a
              key={l.to + l.label}
              href={l.to}
              className={commonCls}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          );
        }
        return (
          <NavLink
            key={l.to}
            to={l.to}
            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `${commonCls} ${isActive ? "!text-[#FF1493]" : ""}`
            }
          >
            {l.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function FuelWidget({ fuel }) {
  if (!fuel) return null;
  return (
    <Link
      to="/fuel"
      data-testid="nav-fuel-widget"
      className="hidden lg:flex items-center gap-2 px-3 py-1.5 border border-[#FFC107]/40 hover:border-[#FFC107] transition"
      title={`$${fuel.total.toLocaleString()} of $${fuel.goal.toLocaleString()} raised`}
    >
      <Fuel size={12} className="text-[#FFC107]" />
      <span className="font-display text-sm tracking-wider text-[#FFC107]">
        ${fuel.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </span>
      <span className="font-mono-tactical text-[10px] text-white/50">
        / ${(fuel.goal / 1000).toFixed(0)}k
      </span>
    </Link>
  );
}

function TourPassBadge({ user }) {
  if (user?.tour_pass_active) {
    const label =
      user?.is_founding_member && user?.tour_pass_member_number
        ? `VIP · #${String(user.tour_pass_member_number).padStart(3, "0")}`
        : "VIP";
    return (
      <Link
        to="/tour-pass"
        data-testid="nav-vip-badge"
        className="flex items-center gap-1.5 bg-[#FFC107] text-[#0A0A0A] px-3 py-1.5 text-xs font-display tracking-[0.2em]"
      >
        <Crown size={12} fill="#0A0A0A" />
        {label}
      </Link>
    );
  }
  return (
    <Link
      to="/tour-pass"
      data-testid="nav-tour-pass"
      className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-semibold text-[#FFC107] hover:text-white transition-colors"
    >
      <Crown size={12} /> Tour Pass
    </Link>
  );
}

function UserActions({ user, onLogout, onDashboard }) {
  if (!user) {
    return (
      <>
        <Link to="/login" data-testid="nav-login" className="btn-ghost text-xs px-4 py-2">
          Login
        </Link>
        <Link to="/register" data-testid="nav-register" className="btn-blaze text-xs px-4 py-2">
          Join the ride
        </Link>
      </>
    );
  }
  return (
    <>
      {user.role === "admin" && (
        <button onClick={onDashboard} data-testid="nav-dashboard" className="btn-ghost text-xs px-4 py-2">
          <Radio size={14} /> Control
        </button>
      )}
      <button onClick={onLogout} data-testid="nav-logout" className="btn-ghost text-xs px-4 py-2">
        {user.name?.split(" ")[0] || "Logout"} · OUT
      </button>
    </>
  );
}

function MobileMenu({ user, open, onClose, onLogout }) {
  if (!open) return null;
  return (
    <div className="md:hidden border-t border-white/10 px-6 py-5 flex flex-col gap-4 bg-[#0A0A0A]/95">
      {LINKS.map((l) => {
        const isHash = l.to.includes("#");
        const cls = "uppercase text-sm tracking-[0.18em] text-white/80";
        const testid = `mnav-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
        if (isHash) {
          return (
            <a key={l.to + l.label} href={l.to} onClick={onClose} className={cls} data-testid={testid}>
              {l.label}
            </a>
          );
        }
        return (
          <Link key={l.to} to={l.to} onClick={onClose} className={cls} data-testid={testid}>
            {l.label}
          </Link>
        );
      })}
      <div className="divider-x my-2" />
      {user ? (
        <button onClick={onLogout} className="btn-ghost" data-testid="mnav-logout">
          Logout
        </button>
      ) : (
        <>
          <Link to="/login" className="btn-ghost text-center" data-testid="mnav-login">
            Login
          </Link>
          <Link to="/register" className="btn-blaze text-center" data-testid="mnav-register">
            Join the ride
          </Link>
        </>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const fuel = useFuelTotals();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const handleMobileLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
        <BrandLink />
        <DesktopNavLinks />

        <div className="hidden md:flex items-center gap-3">
          <FuelWidget fuel={fuel} />
          <TourPassBadge user={user} />
          <UserActions
            user={user}
            onLogout={handleLogout}
            onDashboard={() => navigate("/dashboard")}
          />
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white"
          data-testid="nav-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <MobileMenu
        user={user}
        open={open}
        onClose={() => setOpen(false)}
        onLogout={handleMobileLogout}
      />
    </header>
  );
}
